/**
 * Public footer — the ink surface of the Klargo identity.
 *
 * Ported from Klargo-Reference (src/components/layout/Footer.tsx). It shares
 * the authenticated shell's ink ground, wordmark and hairline grammar so the
 * public page closes in the same voice the logged-in product opens with. The
 * diagonal top edge is the original Klargo silhouette — the one expressive
 * form the public identity keeps.
 *
 * The columns answer a visitor's questions in order: who this is, where to get
 * help, what the terms are, how to reach a person.
 *
 * Every colour here is declared against the ink ground inside Home.css's
 * `.k-footer` block. Nothing outside that block styles this text — the earlier
 * revision set a body colour on `.MuiTypography-*`, which outranked the
 * footer's own `sx` colour and rendered these links at 1.85:1 contrast.
 */

import React from "react";
import { useTranslation } from "react-i18next";
import "./Home.css";
import logoOnInk from "../../assets/klargo16.png";

/** The wedge must open in EXACTLY the colour of the section above it, or its
 *  top edge prints a full-width line. The landing closes on the white CTA
 *  band; other consumers (Security) end on the canvas, so the ground is a prop.
 *  The pre-fold shade keeps the same small step below its ground in both. */
const FOLD_GROUNDS = {
  sheet: { top: "#FFFFFF", shade: "#F1F4F5" },
  canvas: { top: "#F7FAF8", shade: "#F0F4F1" },
};

const LinkedInMark = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const Footer = ({ ground = "sheet" }) => {
  const { t } = useTranslation();
  const fold = FOLD_GROUNDS[ground] || FOLD_GROUNDS.sheet;

  return (
    <footer id="footer" className="k-footer" role="contentinfo">
      {/* Diagonal top edge — the sheet above folding under the footer. The
          wedge starts one pixel above the footer: when the shape's own top
          edge was also the footer's, that edge's antialiasing left a hairline
          of ink showing wherever the boundary landed on a fractional device
          pixel. Lifting it 1px puts the antialiased edge over the section
          above, where partial coverage blends into itself and disappears. */}
      <svg
        className="k-footer-fold"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
        style={{ height: 90 }}
      >
        <defs>
          {/* The shading runs parallel to the fold, not top-to-bottom: a
              vertical gradient has horizontal contour lines, and horizontal
              contours stacked above a diagonal read as a straight edge with a
              diagonal underneath — the very impression this avoids. */}
          <linearGradient id="klargo-footer-fold" x1="0" y1="0" x2="0.5" y2="1">
            <stop offset="0" stopColor={fold.top} />
            <stop offset="0.42" stopColor={fold.top} />
            <stop offset="0.8" stopColor={fold.shade} />
          </linearGradient>
        </defs>
        <polygon points="-1,0 101,0 101,50 -1,100" fill="url(#klargo-footer-fold)" />
      </svg>

      <div className="k-rail k-footer-inner">
        <div className="k-footer-cols">
          {/* Identity. The company's own social account sits here, beside the
              wordmark, rather than as a column heading over a single link. */}
          <div>
            <img className="k-footer-logo" src={logoOnInk} alt="Klargo" />
            <p className="k-footer-tag">{t("footer.tagline")}</p>
            <a
              className="k-footer-link"
              href="https://www.linkedin.com/company/klargo"
              target="_blank"
              rel="noopener noreferrer"
            >
              <LinkedInMark />
              LinkedIn
            </a>
          </div>

          {/* Help and safety — the two real public destinations. */}
          <div>
            <h4>{t("footer.help.title")}</h4>
            {/* Destinations follow the mockup's own routes. Only
                /sakerhet-och-bedragerier resolves in KlargoReact today (it is
                aliased to the Security guide in app-route.jsx); /hjalp and the
                three legal routes have no page yet and will hit the 404. They
                are left pointing at their real paths rather than redirected to
                the security guide, so the gap is visible instead of silently
                landing a visitor on the wrong document. */}
            <ul>
              <li>
                <a className="k-footer-link" href="/hjalp">
                  {t("footer.help.help")}
                </a>
              </li>
              <li>
                <a className="k-footer-link" href="/sakerhet-och-bedragerier">
                  {t("footer.help.security")}
                </a>
              </li>
            </ul>
          </div>

          {/* Policy and terms. */}
          <div>
            <h4>{t("footer.legal.title")}</h4>
            <ul>
              <li>
                <a className="k-footer-link" href="/integritet">
                  {t("footer.legal.privacy")}
                </a>
              </li>
              <li>
                <a className="k-footer-link" href="/villkor">
                  {t("footer.legal.terms")}
                </a>
              </li>
              <li>
                <a className="k-footer-link" href="/cookies">
                  {t("footer.legal.cookies")}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact — the address itself, not a route to it. Only the two
              channels that exist: e-mail and weekday hours. */}
          <div>
            <h4>{t("footer.contact.title")}</h4>
            <a className="k-footer-link" href="mailto:support@klargo.se">
              support@klargo.se
            </a>
            <p className="k-footer-hours">{t("footer.contact.hours")}</p>
          </div>
        </div>

        <div className="k-footer-legal">
          <p>
            © {new Date().getFullYear()} {t("footer.copyright")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
